// INVIMP1 (docs/build-briefs/INVIMP1-inventory-import.md; design/mockups/invimp1/). Pure, no DOM: the rules the Import
// inventory screen trusts. Which file column feeds which field, what blocks the preview, the tabs and counts, and the
// words for each row and for a refusal. The screen itself is ui/src/inventoryImport.ts. Every figure and every status here
// comes from the server; this file only chooses words and holds the person's mapping.

import { isReadAsField, readCellAs } from "../../src/domain/inventoryImportCells.ts";
import type {
  InventoryImportApplyDto,
  InventoryImportPreviewDto,
  InventoryImportPreviewRowDto,
} from "./api.ts";

/** field id to the zero-based index of the file column that feeds it. */
export type Mapping = Record<string, number>;

export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const FILE_ACCEPT = ".csv,.xlsx";

export function hasAllowedExtension(name: string): boolean {
  const lower = name.trim().toLowerCase();
  return lower.endsWith(".csv") || lower.endsWith(".xlsx");
}

/** Why a chosen file is turned away before it is sent, or "" when it is fine. */
export function fileRefusal(file: { name: string; size: number }): string {
  if (!hasAllowedExtension(file.name)) return "CSV or XLSX only";
  if (file.size === 0) return "The file is empty";
  if (file.size > MAX_FILE_BYTES) return "Up to 5 MB";
  return "";
}

export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Puts a field on a column. A field has one column and a column has one field, so whatever held either is released. */
export function assignField(mapping: Mapping, fieldId: string, column: number): Mapping {
  const next: Mapping = {};
  for (const [id, index] of Object.entries(mapping)) {
    if (id !== fieldId && index !== column) next[id] = index;
  }
  next[fieldId] = column;
  return next;
}

/** Releases a column ("Skip this column"). */
export function skipColumn(mapping: Mapping, column: number): Mapping {
  const next: Mapping = {};
  for (const [id, index] of Object.entries(mapping)) {
    if (index !== column) next[id] = index;
  }
  return next;
}

export function fieldOfColumn(mapping: Mapping, column: number): string | undefined {
  return Object.keys(mapping).find((id) => mapping[id] === column);
}

/** "9 of 11 matched": columns that feed a field, of all columns. */
export function matchedLine(mapping: Mapping, columnCount: number): string {
  const matched = new Set(Object.values(mapping)).size;
  return `${matched} of ${columnCount} file columns will be imported`;
}

export type PreviewFix = "name" | "kind";

/** What stops Preview and where the fix is: the name column, then the item kind. */
export interface PreviewBlock {
  reason: string;
  fix: PreviewFix;
  fixLabel: string;
}

/**
 * The item kind is known when a kind column is mapped, one kind is chosen for every row, or the Category column is
 * mapped (the server reads each row's kind from its category; a category it cannot place is that row's error).
 */
export function kindIsKnown(mapping: Mapping, defaultKind: string | undefined): boolean {
  return (
    mapping["itemKind"] !== undefined ||
    mapping["category"] !== undefined ||
    (defaultKind !== undefined && defaultKind !== "")
  );
}

/** What stops Preview, with the button that goes to the fix. Undefined when Preview is ready. */
export function previewBlock(mapping: Mapping, defaultKind: string | undefined): PreviewBlock | undefined {
  if (mapping["name"] === undefined) {
    return {
      reason: "Choose which column is the item name.",
      fix: "name",
      fixLabel: "Choose the name column",
    };
  }
  if (!kindIsKnown(mapping, defaultKind)) {
    return {
      reason: "Tell us what kind of items these are.",
      fix: "kind",
      fixLabel: "Choose the item kind",
    };
  }
  return undefined;
}

/** The sentence beside a disabled Preview, or "" when ready. */
export function previewBlockedReason(mapping: Mapping, defaultKind: string | undefined): string {
  return previewBlock(mapping, defaultKind)?.reason ?? "";
}

export type PreviewTab = "all" | "new" | "update" | "error";
export const PREVIEW_TABS: ReadonlyArray<{ id: PreviewTab; label: string }> = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "update", label: "Update" },
  { id: "error", label: "Errors" },
];

export function tabCount(preview: InventoryImportPreviewDto, tab: PreviewTab): number {
  switch (tab) {
    case "all":
      return preview.counts.rows;
    case "new":
      return preview.counts.new;
    case "update":
      return preview.counts.update;
    case "error":
      return preview.counts.error;
  }
}

export function rowsForTab(
  rows: readonly InventoryImportPreviewRowDto[],
  tab: PreviewTab,
): InventoryImportPreviewRowDto[] {
  return tab === "all" ? [...rows] : rows.filter((row) => row.status === tab);
}

/** The status pill's words for one row. */
export function rowStatusText(row: InventoryImportPreviewRowDto): string {
  if (row.status === "error") return row.reasons[0] ?? "Error";
  if (row.status === "new") return "New";
  return row.matchedBy === "barcode" ? "Update, same barcode" : "Update, same name";
}

export function importButtonText(counts: { new: number; update: number }): string {
  const total = counts.new + counts.update;
  return `Import ${total} ${total === 1 ? "item" : "items"}`;
}

export function resultHeadline(result: Pick<InventoryImportApplyDto, "added" | "updated">): string {
  const total = result.added + result.updated;
  return `${total} ${total === 1 ? "item" : "items"} imported`;
}

export function failedRowsLine(failed: number): string {
  return `${failed} ${failed === 1 ? "row" : "rows"} not imported`;
}

export function failedCsvFileName(fileName: string): string {
  const stem = fileName.replace(/\.[^.]+$/, "").replace(/[^A-Za-z0-9._-]+/g, "-");
  return `${stem === "" ? "import" : stem}-failed-rows.csv`;
}

/** Dollars from integer cents, for the preview's Cost column. */
export function costText(cents: number | null): string {
  if (cents === null) return "-";
  return `$${(cents / 100).toFixed(2)}`;
}

export function orNa(text: string | null | undefined): string {
  return text === null || text === undefined || text === "" ? "-" : text;
}

/** What a refusal from the server reads as. The server's own message is plain language; a lost connection is ours. */
export function errorText(status: number, message: string | undefined): string {
  if (status === 0) return "Could not reach the server. Try again.";
  if (status === 403) return "Not allowed";
  if (message !== undefined && message !== "") return message;
  return "Could not read this file";
}

/** An id for one upload. The same id sent twice is one import. */
export function newUploadId(random: () => string = () => crypto.randomUUID()): string {
  return random();
}

// ---------------------------------------------------------------------
// INVIMP1-F1: examples while mapping, and how a cell will be read
// ---------------------------------------------------------------------

export const EXAMPLE_LIMIT = 3;
export const EXAMPLE_MAX_CHARS = 40;

/** One file column as the inspect route sent it. */
export interface MappingColumn {
  index: number;
  heading: string;
  samples: readonly string[];
}

/** Up to three distinct, non-empty example values, long ones cut. The server already sends this; a screen must not rely on it. */
export function exampleValues(samples: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of samples) {
    const value = raw.trim();
    if (value === "" || seen.has(value)) continue;
    seen.add(value);
    out.push(
      value.length > EXAMPLE_MAX_CHARS ? `${value.slice(0, EXAMPLE_MAX_CHARS - 1)}…` : value,
    );
    if (out.length === EXAMPLE_LIMIT) break;
  }
  return out;
}

/** "ml, oz, cl", or a dash for a column with no values. */
export function examplesText(samples: readonly string[]): string {
  const values = exampleValues(samples);
  return values.length === 0 ? "-" : values.join(", ");
}

/** What each of our fields expects, for the picker. Static, so the person can compare a column against it. */
export const FIELD_EXPECTS: Readonly<Record<string, string>> = {
  name: "Buffalo Trace",
  itemKind: "Liquor",
  category: "Bourbon",
  containerType: "Bottle",
  size: "750 ml",
  sizeUnit: "ml",
  countUnit: "bottle",
  par: "1.5",
  cost: "24.99",
  vendor: "Acme Beverage",
  vendorSku: "BT-1",
  shelfTier: "Well",
  barcode: "012345678905",
  producer: "Buffalo Trace Distillery",
  abv: "45",
  countryRegion: "Kentucky",
  description: "Small batch",
  startingStock: "3",
  sellPrice: "0.85",
};

export function expectsText(fieldId: string): string {
  const example = FIELD_EXPECTS[fieldId];
  return example === undefined ? "" : `Expects ${example}`;
}

/** The file column now feeding a field with its examples, or undefined when the field is not mapped. */
export function fieldSource(
  mapping: Mapping,
  columns: readonly MappingColumn[],
  fieldId: string,
): { heading: string; examples: string } | undefined {
  const index = mapping[fieldId];
  if (index === undefined) return undefined;
  const column = columns.find((candidate) => candidate.index === index);
  if (column === undefined) return undefined;
  return { heading: column.heading, examples: examplesText(column.samples) };
}

export interface ReadAs {
  ok: boolean;
  /** "Example: 750 ml", or the server's reason when it would refuse the cell. */
  text: string;
}

/**
 * How the first example of a mapped column will be read by its field, with the parse the server runs at preview.
 * Undefined when the field is a plain text field (read as typed), the column has no values, or nothing is mapped.
 * For Size, a mapped Size unit column supplies the unit.
 */
export function readAsLine(
  mapping: Mapping,
  columns: readonly MappingColumn[],
  fieldId: string | undefined,
  _fieldLabel: string,
): ReadAs | undefined {
  if (fieldId === "sizeUnit") {
    return mapping["size"] === undefined
      ? { ok: false, text: "Needs a Size column" }
      : readAsLine(mapping, columns, "size", "Size");
  }
  if (fieldId === undefined || !isReadAsField(fieldId)) return undefined;
  const first = (index: number | undefined): string | undefined =>
    index === undefined
      ? undefined
      : exampleValues(columns.find((column) => column.index === index)?.samples ?? [])[0];
  const cell = first(mapping[fieldId]);
  if (cell === undefined) return undefined;
  const parsed = readCellAs(fieldId, cell, first(mapping["sizeUnit"]) ?? "");
  return parsed.ok
    ? { ok: true, text: `Example: ${parsed.value}` }
    : { ok: false, text: parsed.reason };
}
